import React from 'react';

/**
 * Filtres rapides pour accès instantané aux vues courantes
 */
const QuickFilters = ({ documents, activeFilters, onFilterChange }) => {
  // Calculer les compteurs
  const counts = {
    // Types de documents
    devis: documents.filter(d => d.type === 'devis').length,
    bonCommande: documents.filter(d => d.type === 'bon_commande').length,
    facture: documents.filter(d => d.type === 'facture').length,
    // Statuts
    brouillon: documents.filter(d => d.statut === 'brouillon').length,
    envoye: documents.filter(d => d.statut === 'envoyé').length,
    accepte: documents.filter(d => d.statut === 'acceptée').length,
    refuse: documents.filter(d => d.statut === 'refusée').length,
    expire: documents.filter(d => d.statut === 'expiré').length,
    annule: documents.filter(d => d.statut === 'annulé').length,
    nonPaye: documents.filter(d => d.type !== 'devis' && (d.montant_paye || 0) === 0 && (d.total_ttc || 0) > 0).length,
    partiel: documents.filter(d => d.type !== 'devis' && (d.montant_paye || 0) > 0 && (d.reste_a_payer || 0) > 0).length,
    paye: documents.filter(d => d.type !== 'devis' && (d.reste_a_payer || 0) === 0 && (d.montant_paye || 0) > 0).length,
    today: documents.filter(d => {
      const today = new Date().toDateString();
      const docDate = new Date(d.date_creation || d.date).toDateString();
      return today === docDate;
    }).length,
    thisWeek: documents.filter(d => {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      const docDate = new Date(d.date_creation || d.date);
      return docDate >= weekAgo;
    }).length,
    thisMonth: documents.filter(d => {
      const now = new Date();
      const docDate = new Date(d.date_creation || d.date);
      return docDate.getMonth() === now.getMonth() && docDate.getFullYear() === now.getFullYear();
    }).length,
    // Nouveaux filtres Workflow (basés sur type_contact du client)
    offresProspects: documents.filter(d => d.client?.type_contact === 'Client potentiel').length,
    documentsClients: documents.filter(d => d.client?.type_contact === 'Client actuel').length,
    autres: documents.filter(d => d.client?.type_contact === 'Autre' || !d.client?.type_contact).length
  };
  
  const FilterButton = ({ label, count, filter, color = 'gray', icon = '' }) => {
    const isActive = activeFilters.includes(filter);
    
    const colorClasses = {
      gray: isActive ? 'bg-gray-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
      blue: isActive ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-700 hover:bg-blue-200',
      green: isActive ? 'bg-green-600 text-white' : 'bg-green-100 text-green-700 hover:bg-green-200',
      red: isActive ? 'bg-red-600 text-white' : 'bg-red-100 text-red-700 hover:bg-red-200',
      yellow: isActive ? 'bg-yellow-600 text-white' : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200',
      orange: isActive ? 'bg-orange-600 text-white' : 'bg-orange-100 text-orange-700 hover:bg-orange-200',
      purple: isActive ? 'bg-purple-600 text-white' : 'bg-purple-100 text-purple-700 hover:bg-purple-200',
      emerald: isActive ? 'bg-emerald-600 text-white' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
    };
    
    return (
      <button
        onClick={() => onFilterChange(filter)}
        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${colorClasses[color]} ${
          isActive ? 'ring-2 ring-offset-2 ring-' + color + '-500 scale-105' : ''
        }`}
      >
        {icon && <span className="mr-1">{icon}</span>}
        {label} {count > 0 && <span className="ml-1 font-bold">({count})</span>}
      </button>
    );
  };
  
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700">🔍 Filtres Rapides</h3>
        {activeFilters.length > 0 && (
          <button
            onClick={() => onFilterChange('clear')}
            className="text-xs text-gray-500 hover:text-gray-700 underline"
          >
            Effacer tous
          </button>
        )}
      </div>
      
      <div className="space-y-3">
        {/* Types de documents */}
        <div>
          <div className="text-xs text-gray-500 mb-2">Type de document:</div>
          <div className="flex flex-wrap gap-2">
            <FilterButton label="Devis" count={counts.devis} filter="type_devis" color="blue" icon="📋" />
            <FilterButton label="Bons de commande" count={counts.bonCommande} filter="type_bon_commande" color="green" icon="📄" />
            <FilterButton label="Factures" count={counts.facture} filter="type_facture" color="purple" icon="🧾" />
          </div>
        </div>
        
        {/* Statuts Documents */}
        <div>
          <div className="text-xs text-gray-500 mb-2">Statut Document:</div>
          <div className="flex flex-wrap gap-2">
            <FilterButton label="Brouillons" count={counts.brouillon} filter="brouillon" color="gray" icon="📝" />
            <FilterButton label="Envoyés" count={counts.envoye} filter="envoyé" color="blue" icon="📤" />
            <FilterButton label="Acceptés" count={counts.accepte} filter="acceptée" color="green" icon="✅" />
            <FilterButton label="Refusés" count={counts.refuse} filter="refusée" color="red" icon="❌" />
            <FilterButton label="Expirés" count={counts.expire} filter="expiré" color="orange" icon="⏰" />
            <FilterButton label="Annulés" count={counts.annule} filter="annulé" color="purple" icon="🚫" />
          </div>
        </div>
        
        {/* Statuts Paiements */}
        <div>
          <div className="text-xs text-gray-500 mb-2">Statut Paiement:</div>
          <div className="flex flex-wrap gap-2">
            <FilterButton label="Non payés" count={counts.nonPaye} filter="non_paye" color="red" icon="🔴" />
            <FilterButton label="Partiels" count={counts.partiel} filter="partiel" color="yellow" icon="🟡" />
            <FilterButton label="Payés" count={counts.paye} filter="paye" color="green" icon="🟢" />
          </div>
        </div>
        
        {/* Périodes */}
        <div>
          <div className="text-xs text-gray-500 mb-2">Période:</div>
          <div className="flex flex-wrap gap-2">
            <FilterButton label="Aujourd'hui" count={counts.today} filter="today" color="emerald" icon="📅" />
            <FilterButton label="Cette semaine" count={counts.thisWeek} filter="this_week" color="emerald" icon="📅" />
            <FilterButton label="Ce mois" count={counts.thisMonth} filter="this_month" color="emerald" icon="📅" />
          </div>
        </div>
        
        {/* Workflow - Prospects vs Clients */}
        <div>
          <div className="text-xs text-gray-500 mb-2">Type de contact:</div>
          <div className="flex flex-wrap gap-2">
            <FilterButton 
              label="Prospects" 
              count={counts.offresProspects} 
              filter="offres_prospects" 
              color="orange" 
              icon="🎯" 
            />
            <FilterButton 
              label="Clients actuels" 
              count={counts.documentsClients} 
              filter="documents_clients" 
              color="blue" 
              icon="✅" 
            />
            <FilterButton 
              label="Autres" 
              count={counts.autres} 
              filter="autres" 
              color="gray" 
              icon="📄" 
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickFilters;
